<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.SERVICES.PHONE_UPDATE.START"
    :handler="onDialogOpen"
  />
  <VBusHelper
    :reactOn="$constants.EVENTS.SERVICES.PHONE_UPDATE.COMPLETED"
    :handler="
      () => {
        $refs.modal.hide();
      }
    "
  />

  <DialogComponent
    ref="modal"
    type="bottomsheet"
    hideCloseButton
    @closed="onDialogClosed"
  >
    <div class="flex flex-col gap-6 p-4">
      <!-- Étape 0 : Consentement Simple Sauvegarde (Premier numéro pour cet opérateur) -->
      <div
        v-if="currentStep === 'simple-save-consent'"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-2 text-center">
          <div
            class="mx-auto w-16 h-16 bg-blue-50 text-blue-600 flex items-center justify-center rounded-full text-2xl mb-2"
          >
            💾
          </div>
          <h3 class="text-xl font-bold text-primary">
            Sauvegarder ce numéro ?
          </h3>
          <p class="text-gray-500 text-sm">
            C'est la première fois que vous utilisez un numéro
            <strong>{{ operator }}</strong
            >. Voulez-vous enregistrer
            <strong>{{ newPhoneNumber }}</strong> comme votre numéro par défaut
            ?
          </p>
        </div>
        <div class="flex flex-col gap-3 mt-2">
          <button
            @click="confirmSimpleSave"
            class="buttonComponent w-full py-4 rounded-2xl font-bold text-lg"
          >
            Oui, enregistrer
          </button>
          <button
            @click="$refs.modal.hide()"
            class="w-full py-2 text-gray-400 font-medium"
          >
            Pas maintenant
          </button>
        </div>
      </div>

      <!-- Étape 1 : Consentement Validation Payante (Changement de numéro) -->
      <div v-else-if="currentStep === 'consent'" class="flex flex-col gap-4">
        <div class="flex flex-col gap-2 text-center">
          <div
            class="mx-auto w-16 h-16 bg-yellow-50 text-yellow-600 flex items-center justify-center rounded-full text-2xl mb-2"
          >
            🛡️
          </div>
          <h3 class="text-xl font-bold text-primary">Validation de numéro</h3>
          <p class="text-gray-500 text-sm px-2" v-if="enablePayment">
            Pour sécuriser votre compte, la validation d'un nouveau numéro
            entraîne des frais de <strong>{{ verificationFee }} CFA</strong>.
            Voulez-vous valider le numéro
            <strong>{{ newPhoneNumber }}</strong> ?
          </p>
          <p class="text-gray-500 text-sm px-2" v-else>
            Pour sécuriser votre compte, voulez-vous valider le numéro
            <strong>{{ newPhoneNumber }}</strong> ?
          </p>
        </div>
        <div class="flex flex-col gap-3 mt-2">
          <button
            @click="confirmPaymentConsent"
            class="buttonComponent w-full py-4 rounded-2xl font-bold text-lg"
          >
            D'accord, valider
          </button>
          <button
            @click="$refs.modal.hide()"
            class="w-full py-2 text-gray-400 font-medium"
          >
            Annuler
          </button>
        </div>
      </div>

      <!-- Étape 2 : Attente de paiement (Pending) -->
      <div
        v-else-if="currentStep === 'pending'"
        class="flex flex-col items-center gap-6 py-8"
      >
        <div class="relative w-20 h-20">
          <span class="phoneValidationLoader absolute inset-0"></span>
          <div class="absolute inset-0 flex items-center justify-center">
            <ImgComponent
              src="/images/icons/mobile-money.svg"
              class="w-10 h-10"
              alt="Mobile Money"
            />
          </div>
        </div>
        <div class="text-center px-4">
          <h4 class="text-lg font-bold text-primary">
            Vérification du paiement...
          </h4>
          <p class="text-gray-500 text-sm mt-2">
            Veuillez valider la demande de retrait de {{ verificationFee }} CFA
            sur votre téléphone.
          </p>
        </div>
      </div>

      <!-- Étape 3 : Succès Paiement / Attente OTP -->
      <div
        v-else-if="currentStep === 'success'"
        class="flex flex-col items-center gap-6 py-8 text-center px-4"
      >
        <div class="bg-green-100 text-green-600 p-4 rounded-full">
          <svg
            class="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
        </div>
        <div>
          <h4 class="text-lg font-bold text-primary">Paiement confirmé !</h4>
          <p class="text-gray-500 text-sm">
            Un code de validation a été envoyé pour finaliser le changement vers
            le <strong>{{ newPhoneNumber }}</strong
            >.
          </p>
        </div>
        <p class="text-xs text-neutral-400 animate-pulse">
          Ouverture de la fenêtre de validation...
        </p>
      </div>

      <!-- Étape 4 : Erreur -->
      <div
        v-else-if="currentStep === 'failed'"
        class="flex flex-col items-center gap-4 py-6 px-4"
      >
        <div
          class="w-16 h-16 bg-red-100 text-red-600 flex items-center justify-center rounded-full text-3xl"
        >
          ❌
        </div>
        <div class="text-center">
          <h3 class="text-xl font-bold text-primary">Le paiement a échoué</h3>
          <p class="text-red-500 text-sm mt-1">
            {{
              $stores.services.state.phoneUpdate.paymentError ||
              'Une erreur est survenue lors de la transaction.'
            }}
          </p>
        </div>
        <div class="flex flex-col w-full gap-3 mt-4">
          <button
            class="buttonComponent w-full py-4 rounded-2xl font-bold"
            @click="retry"
          >
            Réessayer
          </button>
          <button
            class="w-full py-2 text-gray-400 font-medium"
            @click="$refs.modal.hide()"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const modal = ref(null);

const currentStep = computed(
  () => $stores.services.state.phoneUpdate.paymentStatus
);
const newPhoneNumber = computed(
  () => $stores.services.state.phoneUpdate.newPhoneNumber
);
const operator = computed(() => $stores.services.state.phoneUpdate.operator);
const enablePayment = computed(
  () => $constants.PHONE_UPDATE?.enablePayment !== false
);

const verificationFee = computed(() => {
  const countryCode = $stores.user?.state?.current?.countryCode || 'BJ';
  return (
    $stores.appData?.state?.data?.fees?.[countryCode]?.phoneVerification || 100
  );
});

function onDialogOpen() {
  const store = $stores.services;
  const phoneUpdate = store.state.phoneUpdate;

  const validatedPhones = phoneUpdate.validatedPhones || [];
  const currentOperator = operator.value;

  const hasExistingForOperator = validatedPhones.some(
    (p) =>
      p.carrier?.toUpperCase() === currentOperator?.toUpperCase() ||
      p.operator?.toUpperCase() === currentOperator?.toUpperCase()
  );

  const phoneUpdateConfigs = $constants.PHONE_UPDATE || {};
  const validateFirstPhone = phoneUpdateConfigs.validateFirstPhone;

  // Déterminer l'étape initiale
  if (phoneUpdate.paymentStatus === 'idle' || !phoneUpdate.paymentStatus) {
    if (!validateFirstPhone && !hasExistingForOperator) {
      phoneUpdate.paymentStatus = 'simple-save-consent';
    } else {
      phoneUpdate.paymentStatus = 'consent';
    }
  }

  modal.value.show();
}

function onDialogClosed() {
  // Optionnel: Reset l'état si nécessaire
}

function confirmPaymentConsent() {
  $bus.emit(
    $constants.EVENTS.SERVICES.PHONE_UPDATE.SUBMIT_NEW_NUMBER,
    newPhoneNumber.value
  );
}

function confirmSimpleSave() {
  $bus.emit($constants.EVENTS.PHONE_UPDATE.SIMPLE_SAVE);
}

function retry() {
  $stores.services.state.phoneUpdate.paymentStatus = 'consent';
}
</script>

<style scoped>
.phoneValidationLoader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color, #002e5e);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
